"use client";

import {
 createContext,
 use,
 useState,
 useMemo,
 type ReactNode,
} from "react";
import type { SpecsRecord } from "@/components/specs/types";

export type SearchFlowStage = "search" | "results";
export type MobileViewMode = "list" | "map";
export type ResultsViewMode = "map" | "table" | "grid";

export interface ListingPreviewPhoto {
 url: string;
 slot: string;
}

export interface ListingPreviewData {
 photos: ListingPreviewPhoto[];
 condition: string | null;
 specs: SpecsRecord;
}

interface UIContextValue {
 flowStage: SearchFlowStage;
 setFlowStage: (stage: SearchFlowStage) => void;
 mobileViewMode: MobileViewMode;
 setMobileViewMode: (mode: MobileViewMode) => void;
 resultsViewMode: ResultsViewMode;
 setResultsViewMode: (mode: ResultsViewMode) => void;
 listingPreview: ListingPreviewData | null;
 setListingPreview: (preview: ListingPreviewData | null) => void;
 viewportLocationName: string | null;
 setViewportLocationName: (name: string | null) => void;
 filtersExpanded: boolean;
 setFiltersExpanded: (expanded: boolean) => void;
}

const UIContext = createContext<UIContextValue | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
 const [flowStage, setFlowStage] = useState<SearchFlowStage>("search");
 const [mobileViewMode, setMobileViewMode] = useState<MobileViewMode>("list");
 const [resultsViewMode, setResultsViewMode] =
  useState<ResultsViewMode>("map");
 const [listingPreview, setListingPreview] =
  useState<ListingPreviewData | null>(null);
 const [viewportLocationName, setViewportLocationName] = useState<
  string | null
 >(null);
 const [filtersExpanded, setFiltersExpanded] = useState(false);

 const value = useMemo(
  () => ({
   flowStage,
   setFlowStage,
   mobileViewMode,
   setMobileViewMode,
   resultsViewMode,
   setResultsViewMode,
   listingPreview,
   setListingPreview,
   viewportLocationName,
   setViewportLocationName,
   filtersExpanded,
   setFiltersExpanded,
  }),
  [
   flowStage,
   mobileViewMode,
   resultsViewMode,
   listingPreview,
   viewportLocationName,
   filtersExpanded,
  ],
 );

 return <UIContext value={value}>{children}</UIContext>;
}

const defaultUIContext: UIContextValue = {
 flowStage: "search",
 setFlowStage: () => {},
 mobileViewMode: "list",
 setMobileViewMode: () => {},
 resultsViewMode: "map",
 setResultsViewMode: () => {},
 listingPreview: null,
 setListingPreview: () => {},
 viewportLocationName: null,
 setViewportLocationName: () => {},
 filtersExpanded: false,
 setFiltersExpanded: () => {},
};

export function useUIContext() {
 const context = use(UIContext);
 return context ?? defaultUIContext;
}

export { UIContext };
